import { addDays, format, parseISO } from 'date-fns';
import { productionTiers } from '../data/productionTiers';
import { formatMinutes, formatPercent } from './format';
import type { Agent, AgentRow, FloorDay, FloorFlag, FloorStates, ProductionTier, TierId } from '../types/voip';

export interface FloorRow {
  agent: Agent;
  day: FloorDay;
  prev: FloorDay;
  focus: number;
  prevFocus: number;
  score: number;
  prevScore: number;
  tier: ProductionTier;
  /** Share of logged-in time covered by recorded states */
  accounted: number;
  flags: FloorFlag[];
}

export interface FloorTotals {
  users: number;
  calls: number;
  leads: number;
  loans: number;
  /** Weighted: (On Call + Ready) ÷ Logged in across everyone */
  focus: number;
  score: number;
  wrapUp: number;
}

export interface FloorAward {
  id: 'producer' | 'leads' | 'production' | 'wrap';
  title: string;
  caption: string;
  row: FloorRow;
  value: string;
}

export interface FloorSnapshot {
  rows: FloorRow[];
  current: FloorTotals;
  previous: FloorTotals;
  tiers: { tier: ProductionTier; rows: FloorRow[] }[];
  awards: FloorAward[];
  attention: FloorRow[];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function noise(a: number, b: number): number {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export function previousDate(date: string): string {
  return format(addDays(parseISO(date), -1), 'yyyy-MM-dd');
}

export function dateLabel(date: string, pattern = 'EEE, MMM d'): string {
  return format(parseISO(date), pattern);
}

/** Deterministic Five9 day for a user: a stable personal profile plus daily variation */
export function floorDayFor(agent: Agent, date: string): FloorDay {
  const seed = Number(agent.id.slice(1)) || 1;
  const parsed = parseISO(date);
  const d = Math.round(parsed.getTime() / 86400000);
  const weekend = parsed.getDay() === 0 || parsed.getDay() === 6;
  const daily = (k: number) => noise(seed * 1.7 + k, d);
  const profile = (k: number) => noise(seed, k * 31);

  const warmPhone = profile(9) < 0.05;
  const heavyWrap = profile(8) > 0.93;
  const loggedIn = Math.round((weekend ? 380 : 455) + (daily(1) - 0.5) * 70);
  const baseFocus = warmPhone ? 0.8 + profile(2) * 0.08 : 0.52 + profile(2) * 0.34;
  const focus = clamp(baseFocus + (daily(2) - 0.5) * 0.1, 0.35, 0.93);
  const partial = daily(7) < 0.05;

  const active = Math.round(focus * loggedIn);
  const onCall = Math.round(active * (0.58 + profile(5) * 0.2));
  const ready = active - onCall;
  const wrapUp = Math.round(loggedIn * (heavyWrap ? 0.19 + daily(4) * 0.07 : 0.05 + profile(4) * 0.07 + daily(4) * 0.02));
  const ringing = Math.round(loggedIn * (0.01 + daily(3) * 0.012));
  const gap = Math.round(loggedIn * (partial ? 0.12 + daily(6) * 0.12 : daily(6) * 0.01));
  const notReady = Math.max(0, loggedIn - active - wrapUp - ringing - gap);
  const calls = Math.round(onCall / (2.8 + profile(6) * 2.2));

  const output = profile(7) * (0.7 + daily(8) * 0.6);
  return {
    loggedIn,
    onCall,
    ready,
    wrapUp,
    notReady,
    ringing,
    calls,
    leads: warmPhone ? 0 : Math.round(output * 7),
    loans: warmPhone ? 0 : Math.round(Math.pow(output, 1.4) * 3.2),
  };
}

/** Maps a raw Five9 day onto the four floor-time states */
export function floorStates(day: FloorDay): FloorStates {
  const waiting = day.ready;
  const readyActive = day.ringing;
  const ready = waiting + readyActive;
  const notReady = day.notReady + day.wrapUp;
  return {
    loggedIn: day.loggedIn,
    onCall: day.onCall,
    ready,
    waiting,
    readyActive,
    notReady,
    wrapUp: day.wrapUp,
    unrecorded: Math.max(0, day.loggedIn - day.onCall - ready - notReady),
    focus: day.loggedIn === 0 ? 0 : (day.onCall + ready) / day.loggedIn,
  };
}

export function sumStates(list: FloorStates[]): FloorStates {
  const total = list.reduce(
    (acc, s) => ({
      loggedIn: acc.loggedIn + s.loggedIn,
      onCall: acc.onCall + s.onCall,
      ready: acc.ready + s.ready,
      waiting: acc.waiting + s.waiting,
      readyActive: acc.readyActive + s.readyActive,
      notReady: acc.notReady + s.notReady,
      wrapUp: acc.wrapUp + s.wrapUp,
      unrecorded: acc.unrecorded + s.unrecorded,
      focus: 0,
    }),
    { loggedIn: 0, onCall: 0, ready: 0, waiting: 0, readyActive: 0, notReady: 0, wrapUp: 0, unrecorded: 0, focus: 0 }
  );
  return { ...total, focus: total.loggedIn === 0 ? 0 : (total.onCall + total.ready) / total.loggedIn };
}

export function focusOf(day: FloorDay): number {
  return floorStates(day).focus;
}

export function accountedOf(day: FloorDay): number {
  if (day.loggedIn === 0) return 1;
  return (day.onCall + day.ready + day.wrapUp + day.notReady + day.ringing) / day.loggedIn;
}

/** 0–100: 40% effort (focus) + 60% output (leads converted, loans submitted) */
export function productionScore(day: FloorDay): number {
  const effort = clamp(focusOf(day) * 100, 0, 100);
  const output = clamp(day.leads * 7 + day.loans * 18, 0, 100);
  return Math.round(0.4 * effort + 0.6 * output);
}

export function tierFor(score: number): ProductionTier {
  return productionTiers.find((t) => score >= t.min) ?? productionTiers[productionTiers.length - 1];
}

function flagsFor(day: FloorDay, focus: number, accounted: number, answerRate: number): FloorFlag[] {
  const flags: FloorFlag[] = [];
  if (focus >= 0.75 && day.leads + day.loans === 0) {
    flags.push({ kind: 'warm', severity: 4, label: 'Warm phone', detail: `${formatPercent(focus)} focus, no output` });
  }
  if (day.loggedIn > 0 && day.wrapUp / day.loggedIn >= 0.18) {
    flags.push({ kind: 'wrap', severity: 3, label: 'Heavy wrap-up', detail: `${formatMinutes(day.wrapUp)} in wrap-up` });
  }
  if (answerRate < 0.85) {
    flags.push({ kind: 'answer', severity: answerRate < 0.75 ? 3 : 2, label: 'Low answer rate', detail: `${formatPercent(answerRate)} answered` });
  }
  if (accounted < 0.9) {
    flags.push({ kind: 'partial', severity: 1, label: 'Partial data', detail: `${formatPercent(accounted)} of login accounted for` });
  }
  return flags.sort((a, b) => b.severity - a.severity);
}

export function buildFloorRows(date: string, rows: AgentRow[]): FloorRow[] {
  const prevDate = previousDate(date);
  return rows.map(({ agent, stats }) => {
    const day = floorDayFor(agent, date);
    const prev = floorDayFor(agent, prevDate);
    const focus = focusOf(day);
    const accounted = accountedOf(day);
    const score = productionScore(day);
    return {
      agent,
      day,
      prev,
      focus,
      prevFocus: focusOf(prev),
      score,
      prevScore: productionScore(prev),
      tier: tierFor(score),
      accounted,
      flags: flagsFor(day, focus, accounted, stats.answerRate),
    };
  });
}

export function floorTotals(rows: FloorRow[], which: 'day' | 'prev' = 'day'): FloorTotals {
  const days = rows.map((r) => r[which]);
  const sum = (pick: (d: FloorDay) => number) => days.reduce((s, d) => s + pick(d), 0);
  const logged = sum((d) => d.loggedIn);
  return {
    users: rows.length,
    calls: sum((d) => d.calls),
    leads: sum((d) => d.leads),
    loans: sum((d) => d.loans),
    focus: logged === 0 ? 0 : sum((d) => d.onCall + d.ready + d.ringing) / logged,
    score: rows.length === 0 ? 0 : Math.round((rows.reduce((s, r) => s + (which === 'day' ? r.score : r.prevScore), 0) / rows.length) * 10) / 10,
    wrapUp: sum((d) => d.wrapUp),
  };
}

function top(rows: FloorRow[], compare: (a: FloorRow, b: FloorRow) => number): FloorRow | undefined {
  return [...rows].sort(compare)[0];
}

function buildAwards(rows: FloorRow[]): FloorAward[] {
  if (rows.length === 0) return [];
  const awards: FloorAward[] = [];
  const producer = top(rows, (a, b) => b.day.loans - a.day.loans || b.day.leads - a.day.leads || b.score - a.score);
  const leads = top(rows, (a, b) => b.day.leads - a.day.leads || b.day.loans - a.day.loans);
  const production = top(rows, (a, b) => b.score - a.score);
  const active = rows.filter((r) => r.day.calls >= 20);
  const wrap = top(active.length ? active : rows, (a, b) => a.day.wrapUp - b.day.wrapUp);

  if (producer) awards.push({ id: 'producer', title: 'Top Producer', caption: 'Most loans submitted to underwriting', row: producer, value: `${producer.day.loans} loans submitted` });
  if (leads) awards.push({ id: 'leads', title: 'Lead Magnet', caption: 'Most leads converted', row: leads, value: `${leads.day.leads} leads converted` });
  if (production) awards.push({ id: 'production', title: 'Best Production', caption: 'Highest production score', row: production, value: `Score ${production.score}` });
  if (wrap) awards.push({ id: 'wrap', title: 'Tightest Wrap', caption: 'Least after-call work', row: wrap, value: `${formatMinutes(wrap.day.wrapUp)} wrap-up` });
  return awards;
}

export function summarizeFloor(rows: FloorRow[]): FloorSnapshot {
  const severity = (r: FloorRow) => r.flags.reduce((s, f) => s + f.severity, 0);
  return {
    rows,
    current: floorTotals(rows, 'day'),
    previous: floorTotals(rows, 'prev'),
    tiers: productionTiers.map((tier) => ({
      tier,
      rows: rows.filter((r) => r.tier.id === tier.id).sort((a, b) => b.score - a.score),
    })),
    awards: buildAwards(rows),
    attention: rows
      .filter((r) => r.flags.length > 0)
      .sort((a, b) => b.flags[0].severity - a.flags[0].severity || severity(b) - severity(a) || a.score - b.score),
  };
}

export function tierRank(id: TierId): number {
  return productionTiers.findIndex((t) => t.id === id);
}
